import {
  Banner,
  Icon,
  LegacyStack,
  Modal,
  ResourceItem,
  ResourceList,
  Spinner,
  Text,
  TextField,
  VerticalStack,
} from '@shopify/polaris';
import { SearchMinor } from '@shopify/polaris-icons';
import { useEffect, useState } from 'react';
import { getProducts, getProdutById } from '../../../services/productService';

/**
 * "Copiar de otra serie": busca una familia existente y devuelve sus datos para
 * prerrellenar el formulario de alta.
 *
 * Lo que NO se copia, y por qué:
 *   - EAN (barcode) y UPC: son identificadores únicos del catálogo. Se vacían.
 *   - SKU: también es único. Se conserva solo el sufijo de talla, porque el
 *     prefijo lo reescribe el formulario con la familia nueva.
 *   - family: es lo que distingue al producto nuevo; la escribe el usuario.
 *   - imágenes: se copian. Son las mismas fotos de estudio en la mayoría de
 *     casos y el usuario puede quitarlas con un clic.
 *
 * Se busca con `empty: true` para que aparezcan también las series sin stock:
 * si no, no se podría copiar de una serie agotada, que es justo el caso
 * habitual cuando se repone una temporada.
 */
const CopyFromSeriesModal = ({ open, onClose, onCopy }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setResults([]);
      setError(null);
      return;
    }
  }, [open]);

  useEffect(() => {
    const term = search.trim();
    if (term.length < 3) {
      setResults([]);
      return undefined;
    }

    setIsSearching(true);
    const timer = setTimeout(() => {
      getProducts({ s: term, limit: 20, page: 1, empty: true })
        .then((r) => {
          // El listado devuelve una fila por barcode/depósito: agrupamos por familia.
          const byFamily = new Map();
          (r.data?.data ?? []).forEach((row) => {
            if (!byFamily.has(row.family)) byFamily.set(row.family, row);
          });
          setResults([...byFamily.values()]);
          setError(null);
        })
        .catch(() => setError('No se pudo buscar. Inténtalo de nuevo.'))
        .finally(() => setIsSearching(false));
    }, 400);

    return () => clearTimeout(timer);
  }, [search]);

  const handleSelect = (product) => {
    const productId = product.parentId ?? product.id;
    if (!productId) return;

    setIsLoadingDetail(true);
    getProdutById(productId)
      .then((r) => {
        onCopy(r.data);
        onClose();
      })
      .catch(() => setError('No se pudo cargar la serie seleccionada.'))
      .finally(() => setIsLoadingDetail(false));
  };

  return (
    <Modal open={open} onClose={onClose} title="Copiar de otra serie" secondaryActions={[{ content: 'Cancelar', onAction: onClose }]}>
      <Modal.Section>
        <VerticalStack gap="4">
          <Banner status="info">
            Se copian los datos generales, los tags, las imágenes y las tallas. El EAN y el UPC se dejan vacíos porque no
            se pueden repetir, y el SKU conserva solo el sufijo de talla.
          </Banner>

          <TextField
            label="Buscar serie"
            labelHidden
            prefix={<Icon source={SearchMinor} color="inkLighter" />}
            placeholder="Familia, SKU, EAN o nombre (mínimo 3 caracteres)"
            value={search}
            onChange={setSearch}
            autoComplete="off"
            clearButton
            onClearButtonClick={() => setSearch('')}
          />

          {error && <Banner status="critical">{error}</Banner>}

          {(isSearching || isLoadingDetail) && (
            <LegacyStack alignment="center" spacing="tight">
              <Spinner size="small" />
              <Text variant="bodySm" color="subdued">
                {isLoadingDetail ? 'Cargando la serie…' : 'Buscando…'}
              </Text>
            </LegacyStack>
          )}

          {!isSearching && search.trim().length >= 3 && results.length === 0 && !error && (
            <Text variant="bodySm" color="subdued">
              Ninguna serie coincide con «{search}».
            </Text>
          )}

          {results.length > 0 && (
            <ResourceList
              resourceName={{ singular: 'serie', plural: 'series' }}
              items={results}
              renderItem={(product) => (
                <ResourceItem
                  id={String(product.parentId ?? product.id)}
                  onClick={() => handleSelect(product)}
                  accessibilityLabel={`Copiar de la serie ${product.family}`}
                >
                  <Text variant="bodyMd" fontWeight="semibold">
                    {product.family}
                  </Text>
                  <Text variant="bodySm" color="subdued">
                    {product.title || 'Sin nombre'}
                  </Text>
                </ResourceItem>
              )}
            />
          )}
        </VerticalStack>
      </Modal.Section>
    </Modal>
  );
};

export default CopyFromSeriesModal;
